import { useEffect, useState } from "react";
import { CalendarClock, Dog, HeartPulse, Loader2, Mail, Phone, Scissors, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../../lib/api";

const STATUS_STYLES = {
  new: "bg-lavender-700 text-white",
  contacted: "bg-amber-100 text-amber-800",
  booked: "bg-grass-100 text-grass-700",
};

export default function BookingsTab() {
  const [bookings, setBookings] = useState(null);

  const load = () =>
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => toast.error("Could not load booking requests"));

  useEffect(() => { load(); }, []);

  const setStatus = async (b, status) => {
    try {
      await api.patch(`/admin/bookings/${b.id}`, { status });
      setBookings((bs) => bs.map((x) => (x.id === b.id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      setBookings((bs) => bs.filter((x) => x.id !== b.id));
      toast.success("Request deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!bookings) {
    return <div className="grid place-items-center py-20" data-testid="bookings-loading"><Loader2 className="animate-spin text-lavender-600" size={26} /></div>;
  }

  if (!bookings.length) {
    return (
      <div data-testid="bookings-empty" className="rounded-3xl border-2 border-dashed border-lavender-300 bg-white p-14 text-center">
        <Dog size={32} className="mx-auto text-lavender-400" />
        <p className="mt-4 font-serif text-xl text-stone-800">No booking requests yet</p>
        <p className="mt-1 text-sm text-stone-500">Requests from the website's appointment form will land here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="bookings-list">
      <p className="text-sm text-stone-500">Newest first. Mark each request as you work through it.</p>
      {bookings.map((b, i) => (
        <div key={b.id} data-testid={`booking-card-${i}`} className="rounded-3xl border border-lavender-700/10 bg-white p-6 shadow-soft">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-serif text-xl text-stone-900">{b.name}</h3>
                <span data-testid={`booking-status-${i}`} className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${STATUS_STYLES[b.status] || STATUS_STYLES.new}`}>
                  {b.status}
                </span>
              </div>
              <p className="mt-1 text-xs text-stone-400">
                {b.created_at ? new Date(b.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : ""}
              </p>
            </div>
            <button onClick={() => remove(b)} data-testid={`booking-delete-${i}`} aria-label="Delete request" className="grid h-8 w-8 place-items-center rounded-full bg-red-50 text-red-500 transition-colors hover:bg-red-100">
              <Trash2 size={14} />
            </button>
          </div>

          <div className="mt-4 grid gap-2.5 text-sm text-stone-700 sm:grid-cols-2">
            {b.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-lavender-600" /><a className="hover:text-lavender-700" href={`tel:${b.phone}`}>{b.phone}</a></p>}
            {b.email && <p className="flex items-center gap-2 break-all"><Mail size={14} className="shrink-0 text-lavender-600" /><a className="hover:text-lavender-700" href={`mailto:${b.email}`}>{b.email}</a></p>}
            {(b.dogName || b.dogBreed) && <p className="flex items-center gap-2" data-testid={`booking-dog-${i}`}><Dog size={14} className="text-lavender-600" />{[b.dogName, b.dogBreed, b.dogAge, b.dogWeight].filter(Boolean).join(" · ")}</p>}
            {b.service && <p className="flex items-center gap-2"><Scissors size={14} className="text-lavender-600" />{b.service}</p>}
            {b.preferredDate && <p className="flex items-center gap-2"><CalendarClock size={14} className="text-lavender-600" />{b.preferredDate}</p>}
            {b.healthConcerns && (
              <p className="flex items-start gap-2 sm:col-span-2" data-testid={`booking-health-${i}`}>
                <HeartPulse size={14} className="mt-0.5 shrink-0 text-lavender-600" />
                <span>{b.healthConcerns === "yes" ? `Health concerns: ${b.healthDetails}` : "No health concerns reported"}</span>
              </p>
            )}
          </div>
          {b.notes && <p className="mt-3 rounded-2xl bg-lavender-50 px-4 py-3 text-sm italic text-stone-600">“{b.notes}”</p>}

          <div className="mt-5 flex flex-wrap gap-2.5">
            {b.status !== "contacted" && (
              <button onClick={() => setStatus(b, "contacted")} data-testid={`booking-contacted-button-${i}`} className="rounded-full bg-amber-100 px-4 py-2 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-200">
                Mark contacted
              </button>
            )}
            {b.status !== "booked" && (
              <button onClick={() => setStatus(b, "booked")} data-testid={`booking-booked-button-${i}`} className="rounded-full bg-grass-100 px-4 py-2 text-xs font-semibold text-grass-700 transition-colors hover:bg-grass-500/25">
                Mark booked
              </button>
            )}
            {b.status !== "new" && (
              <button onClick={() => setStatus(b, "new")} data-testid={`booking-reopen-button-${i}`} className="rounded-full bg-lavender-100 px-4 py-2 text-xs font-semibold text-lavender-800 transition-colors hover:bg-lavender-200">
                Reopen
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
